import { Plus, Trash2 } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";

export function GroupField({ field, value, onChange, testIdPrefix }) {
  const maxSets = field.max_sets || 6;
  const minSets = field.min_sets || 1;
  const base = Array.isArray(value) && value.length > 0 ? value : [];
  const sets = base.length >= minSets ? base : [...base, ...Array.from({ length: minSets - base.length }, () => ({}))];
  const update = (idx, sid, v) => onChange(sets.map((s, i) => (i === idx ? { ...s, [sid]: v } : s)));
  const addSet = () => onChange([...sets, {}]);
  const removeSet = (idx) => onChange(sets.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-group`}>
      {sets.map((set, i) => (
        <div key={i} className="border border-[#E5E5E5] rounded-xl p-4 bg-white space-y-3" data-testid={`${testIdPrefix}-set-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-neutral-400">
              {field.label} #{i + 1}
            </span>
            {sets.length > minSets && (
              <button
                type="button"
                onClick={() => removeSet(i)}
                className="text-neutral-400 hover:text-red-600 transition-colors duration-150"
                data-testid={`${testIdPrefix}-set-${i}-remove`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {(field.subfields || []).map((sf) => (
            <div key={sf.id} className="space-y-1.5">
              <Label>
                {sf.label} {sf.required && <span className="text-red-500">*</span>}
              </Label>
              {sf.type === "textarea" ? (
                <Textarea
                  value={set[sf.id] || ""}
                  onChange={(e) => update(i, sf.id, e.target.value)}
                  placeholder={sf.placeholder}
                  rows={3}
                  className="bg-white"
                  data-testid={`${testIdPrefix}-set-${i}-${sf.id}`}
                />
              ) : (
                <Input
                  type={sf.type === "phone" ? "tel" : sf.type}
                  value={set[sf.id] || ""}
                  onChange={(e) => update(i, sf.id, e.target.value)}
                  placeholder={sf.placeholder}
                  className="bg-white"
                  data-testid={`${testIdPrefix}-set-${i}-${sf.id}`}
                />
              )}
            </div>
          ))}
        </div>
      ))}
      {sets.length < maxSets && (
        <button
          type="button"
          onClick={addSet}
          className="w-full flex items-center justify-center gap-2 text-sm border border-dashed border-neutral-300 rounded-xl px-4 py-3 hover:border-neutral-500 transition-colors duration-150"
          data-testid={`${testIdPrefix}-add-set`}
        >
          <Plus className="h-4 w-4" /> Add another
        </button>
      )}
    </div>
  );
}

export function ProductListField({ field, value, onChange, testIdPrefix }) {
  const items = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const setItem = (pid, patch) => onChange({ ...items, [pid]: { ...(items[pid] || {}), ...patch } });
  const products = field.products || [];
  const total = products.reduce((sum, p) => {
    const q = parseFloat(items[p.id]?.qty);
    return p.counts_to_total && p.price_value && !isNaN(q) && q > 0 ? sum + q * p.price_value : sum;
  }, 0);

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-products`}>
      {products.length === 0 && <p className="text-sm text-neutral-400">No products listed.</p>}
      {products.map((p) => {
        const item = items[p.id] || {};
        const showQty = p.allow_qty !== false;
        const showNote = p.allow_note !== false;
        return (
          <div key={p.id} className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 space-y-2" data-testid={`${testIdPrefix}-product-${p.id}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium text-sm">{p.name}</span>
              {p.price_label && <span className="text-xs text-neutral-500 shrink-0">{p.price_label}</span>}
            </div>
            {(showQty || showNote) && (
              <div className={`grid gap-2 ${showQty && showNote ? "grid-cols-[110px_1fr]" : "grid-cols-1"}`}>
                {showQty && (
                  <Input
                    type="number" min="0" step="any"
                    value={item.qty ?? ""}
                    onChange={(e) => setItem(p.id, { qty: e.target.value })}
                    placeholder={p.qty_hint || "Qty"}
                    className="bg-white h-9 text-sm"
                    data-testid={`${testIdPrefix}-product-${p.id}-qty`}
                  />
                )}
                {showNote && (
                  <Input
                    value={item.note ?? ""}
                    onChange={(e) => setItem(p.id, { note: e.target.value })}
                    placeholder={p.note_label || "Pack / cut size"}
                    className="bg-white h-9 text-sm"
                    data-testid={`${testIdPrefix}-product-${p.id}-note`}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
      {total > 0 && (
        <div className="flex justify-end pt-1" data-testid={`${testIdPrefix}-total`}>
          <span className="text-sm font-semibold">
            Estimated total: £{total.toFixed(2)}{" "}
            <span className="font-normal text-xs text-neutral-400">(fixed-price items only)</span>
          </span>
        </div>
      )}
    </div>
  );
}

export function CheckboxGroup({ field, value, onChange, testIdPrefix }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt, checked) =>
    onChange(checked ? [...selected, opt] : selected.filter((o) => o !== opt));

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      {(field.options || []).map((o) => (
        <div key={o} className="flex items-center gap-3 bg-white border border-[#E5E5E5] rounded-xl px-4 py-3">
          <Checkbox
            id={`${testIdPrefix}-${o}`}
            checked={selected.includes(o)}
            onCheckedChange={(v) => toggle(o, !!v)}
            data-testid={`${testIdPrefix}-option-${o}`}
          />
          <Label htmlFor={`${testIdPrefix}-${o}`} className="font-normal cursor-pointer">{o}</Label>
        </div>
      ))}
    </div>
  );
}
